import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserProfile } from "@/components/UserProfile";
import { api, CreateUserData } from "@/lib/api";
import { DotsThree, Eye, EyeSlash, FileArrowDown, FileArrowUp, IdentificationCard, Key, MagnifyingGlass, PencilSimple, ShieldCheck, SignOut, Trash, UserGear, UserPlus, Users } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const DEPARTMENTS = [
  "Engineering",
  "Product",
  "Design",
  "Marketing",
  "Sales",
  "Human Resources",
  "Finance",
  "Operations",
  "Customer Support",
  "General"
];

const AVATAR_OPTIONS = ["👤", "👩", "👨", "👩‍💼", "👨‍💼", "👩‍💻", "👨‍💻", "🧑‍💼", "🧑‍💻", "👷", "👮", "🦸", "🧑‍🎨", "🧑‍🔬"];

interface UserManagementProps {
  currentUser: any;
  onInitiateOffboarding?: (user: any, lastWorkingDay: string, reason: string, reasonDetails?: string) => void;
}

export function UserManagement({ currentUser, onInitiateOffboarding }: UserManagementProps) {
  const [users, setUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [filterDepartment, setFilterDepartment] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Edit user dialog
  const [editingUser, setEditingUser] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [newDepartment, setNewDepartment] = useState("");

  // Create user dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserData>({
    email: "",
    password: "",
    name: "",
    department: "General",
    role: "employee",
    phone: "",
    avatar: "👤"
  });
  const [showPassword, setShowPassword] = useState(false);

  // Reset password dialog
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Bulk operations dialog
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'role' | 'department' | 'activate' | 'deactivate'>('role');
  const [bulkRole, setBulkRole] = useState<string>("employee");
  const [bulkDepartment, setBulkDepartment] = useState<string>("General");

  // Import dialog
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any[]>([]);

  // Profile view
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Offboarding dialog
  const [isOffboardingDialogOpen, setIsOffboardingDialogOpen] = useState(false);
  const [offboardingUser, setOffboardingUser] = useState<any>(null);
  const [offboardingForm, setOffboardingForm] = useState({
    lastWorkingDay: '',
    reason: 'resignation',
    reasonDetails: ''
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  const loadUsers = async () => {
    try {
      const data = await api.getUsers();
      setUsers(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load users");
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = filterRole === "all" || user.role === filterRole;
    const matchesDepartment = filterDepartment === "all" || user.department === filterDepartment;
    const matchesActive = showInactive || user.is_active !== 0;
    
    // Superadmin protection: Admin users cannot view/manage superadmin users
    const canViewUser = currentUser?.role === "superadmin" || user.role !== "superadmin";

    return matchesSearch && matchesRole && matchesDepartment && matchesActive && canViewUser;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  // Get unique departments from users
  const uniqueDepartments = [...new Set(users.map(u => u.department).filter(Boolean))];

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password || !createForm.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const newUser = await api.createUser(createForm);
      setUsers([{ ...newUser, joinedAt: new Date().toISOString(), is_active: 1 }, ...users]);
      setIsCreateDialogOpen(false);
      setCreateForm({
        email: "",
        password: "",
        name: "",
        department: "General",
        role: "employee",
        phone: "",
        avatar: "👤"
      });
      toast.success("User created successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to create user");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const userToDelete = users.find(u => u.id === userId);
    
    // Superadmin protection
    if (userToDelete?.role === 'superadmin') {
      if (currentUser?.role !== 'superadmin') {
        toast.error("You don't have permission to delete Superadmin users");
        return;
      }
      toast.error("Cannot delete a Superadmin user");
      return;
    }

    if (confirm("Are you sure you want to deactivate this user? They will no longer be able to log in.")) {
      try {
        await api.deleteUser(userId);
        setUsers(users.map(u => u.id === userId ? { ...u, is_active: 0 } : u));
        toast.success("User deactivated successfully");
      } catch (error) {
        toast.error("Failed to deactivate user");
      }
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      await api.activateUser(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: 1 } : u));
      toast.success("User activated successfully");
    } catch (error) {
      toast.error("Failed to activate user");
    }
  };

  const handleEditRole = (user: any) => {
    // Superadmin protection: Admin cannot edit superadmin users
    if (user.role === 'superadmin' && currentUser?.role !== 'superadmin') {
      toast.error("You don't have permission to edit Superadmin users");
      return;
    }
    setEditingUser(user);
    setNewRole(user.role);
    setNewDepartment(user.department || "General");
    setIsEditDialogOpen(true);
  };

  const saveUserChanges = async () => {
    if (editingUser) {
      try {
        await api.updateUser(editingUser.id, { role: newRole, department: newDepartment });
        const updatedUsers = users.map(u =>
          u.id === editingUser.id ? { ...u, role: newRole, department: newDepartment } : u
        );
        setUsers(updatedUsers);
        setIsEditDialogOpen(false);
        setEditingUser(null);
        toast.success("User updated successfully");
      } catch (error) {
        toast.error("Failed to update user");
      }
    }
  };

  const handleResetPassword = (user: any) => {
    setResetPasswordUser(user);
    setNewPassword("");
    setIsResetPasswordDialogOpen(true);
  };

  const saveNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await api.adminResetPassword(resetPasswordUser.id, newPassword);
      setIsResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword("");
      toast.success("Password reset successfully");
    } catch (error) {
      toast.error("Failed to reset password");
    }
  };

  // Bulk operations handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers(new Set());
      setSelectAll(false);
    } else {
      const allUserIds = new Set(paginatedUsers.filter(u => u.role !== 'superadmin').map(u => u.id));
      setSelectedUsers(allUserIds);
      setSelectAll(true);
    }
  };

  const handleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
    setSelectAll(newSelected.size === paginatedUsers.filter(u => u.role !== 'superadmin').length);
  };

  const handleBulkAction = async () => {
    if (selectedUsers.size === 0) {
      toast.error("No users selected");
      return;
    }

    const selectedArray = Array.from(selectedUsers);

    try {
      if (bulkAction === 'activate' || bulkAction === 'deactivate') {
        const action = bulkAction === 'activate' ? 'activate' : 'deactivate';
        await Promise.all(
          selectedArray.map(userId =>
            action === 'activate' ? api.activateUser(userId) : api.deleteUser(userId)
          )
        );

        setUsers(users.map(u =>
          selectedUsers.has(u.id) ? { ...u, is_active: action === 'activate' ? 1 : 0 } : u
        ));

        toast.success(`${selectedUsers.size} users ${action}d successfully`);
      } else if (bulkAction === 'role') {
        await Promise.all(
          selectedArray.map(userId =>
            api.updateUser(userId, { role: bulkRole })
          )
        );

        setUsers(users.map(u =>
          selectedUsers.has(u.id) ? { ...u, role: bulkRole } : u
        ));

        toast.success(`Role updated for ${selectedUsers.size} users`);
      } else if (bulkAction === 'department') {
        await Promise.all(
          selectedArray.map(userId =>
            api.updateUser(userId, { department: bulkDepartment })
          )
        );

        setUsers(users.map(u =>
          selectedUsers.has(u.id) ? { ...u, department: bulkDepartment } : u
        ));

        toast.success(`Department updated for ${selectedUsers.size} users`);
      }

      setIsBulkDialogOpen(false);
      setSelectedUsers(new Set());
      setSelectAll(false);
    } catch (error: any) {
      toast.error(error.message || "Bulk operation failed");
    }
  };

  const handleExportUsers = () => {
    const exportData = filteredUsers.map(user => ({
      Name: user.name,
      Email: user.email,
      Role: user.role,
      Department: user.department,
      Phone: user.phone || '',
      Status: user.is_active === 0 ? 'Inactive' : 'Active',
      'Joined Date': user.joinedAt ? new Date(user.joinedAt).toLocaleDateString() : 'N/A',
      'Last Login': user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0] || {});
    const csvContent = [
      headers.join(','),
      ...exportData.map(row =>
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape quotes and wrap in quotes if contains comma
          return typeof value === 'string' && (value.includes(',') || value.includes('"'))
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        }).join(',')
      )
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${exportData.length} users to CSV`);
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        toast.error("Invalid CSV file");
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const preview = lines.slice(1, 11).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const user: any = {};
        headers.forEach((header, index) => {
          user[header.toLowerCase()] = values[index];
        });
        return user;
      });

      setImportPreview(preview);
    };

    reader.readAsText(file);
  };

  const handleImportUsers = async () => {
    if (!importFile || importPreview.length === 0) {
      toast.error("No valid import data");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

        let imported = 0;
        let failed = 0;

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const userData: any = {};
          headers.forEach((header, index) => {
            userData[header] = values[index];
          });

          try {
            await api.createUser({
              name: userData.name,
              email: userData.email,
              password: userData.password || 'Welcome@123',
              role: userData.role || 'employee',
              department: userData.department || 'General',
              phone: userData.phone || '',
              avatar: userData.avatar || '👤'
            });
            imported++;
          } catch (error) {
            failed++;
            console.error(`Failed to import user: ${userData.email}`, error);
          }
        }

        setIsImportDialogOpen(false);
        setImportFile(null);
        setImportPreview([]);
        loadUsers();

        toast.success(`Imported ${imported} users${failed > 0 ? `, ${failed} failed` : ''}`);
      } catch (error) {
        toast.error("Import failed");
      }
    };

    reader.readAsText(importFile);
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'bg-purple-600 text-white dark:bg-purple-500 dark:text-white';
      case 'admin':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
      case 'hr':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'manager':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300">
            User Management
          </h2>
          <p className="text-slate-500 dark:text-slate-400">
            Manage system access and user roles • {filteredUsers.length} users
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <MagnifyingGlass className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search users..."
              className="pl-9 bg-white/50 dark:bg-slate-950/50"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Bulk Actions */}
          {selectedUsers.size > 0 && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setIsBulkDialogOpen(true)}
            >
              <Users className="h-4 w-4" />
              Bulk ({selectedUsers.size})
            </Button>
          )}

          {/* Export Button */}
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleExportUsers}
          >
            <FileArrowDown className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>

          {/* Import Button */}
          <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <FileArrowUp className="h-4 w-4" />
                <span className="hidden sm:inline">Import</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Import Users from CSV</DialogTitle>
                <DialogDescription>
                  Upload a CSV file with columns: name, email, password, role, department, phone
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>CSV File</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleImportFile}
                  />
                  <p className="text-xs text-slate-500">
                    CSV format: name,email,password,role,department,phone
                  </p>
                </div>
                {importPreview.length > 0 && (
                  <div className="space-y-2">
                    <Label>Preview (first 10 rows)</Label>
                    <div className="border rounded-md p-3 max-h-60 overflow-auto">
                      <pre className="text-xs">
                        {importPreview.map((user, i) => (
                          <div key={i} className="mb-1">
                            {user.name} - {user.email} ({user.role})
                          </div>
                        ))}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsImportDialogOpen(false);
                  setImportFile(null);
                  setImportPreview([]);
                }}>Cancel</Button>
                <Button onClick={handleImportUsers} disabled={!importFile}>
                  Import Users
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            {(currentUser?.role === 'superadmin' || currentUser?.role === 'admin' || currentUser?.role === 'hr') && (
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Add User</span>
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new user to the system. They will receive login credentials.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    className="col-span-3"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="email" className="text-right">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    className="col-span-3"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="password" className="text-right">Password *</Label>
                  <div className="col-span-3 relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      className="pr-10"
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="phone" className="text-right">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="+1 (555) 000-0000"
                    className="col-span-3"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">Role</Label>
                  <Select
                    value={createForm.role}
                    onValueChange={(value: any) => setCreateForm({ ...createForm, role: value })}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="superadmin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">Department</Label>
                  <Select
                    value={createForm.department}
                    onValueChange={(value) => setCreateForm({ ...createForm, department: value })}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((dept) => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">Avatar</Label>
                  <div className="col-span-3 flex flex-wrap gap-2">
                    {AVATAR_OPTIONS.map((avatar) => (
                      <Button
                        key={avatar}
                        type="button"
                        variant={createForm.avatar === avatar ? "default" : "outline"}
                        size="sm"
                        className="w-10 h-10 text-lg"
                        onClick={() => setCreateForm({ ...createForm, avatar })}
                      >
                        {avatar}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateUser}>Create User</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="backdrop-blur-xl bg-white/70 dark:bg-slate-900/70 border-white/20 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-500">Role:</Label>
              <Select value={filterRole} onValueChange={(v) => { setFilterRole(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-500">Department:</Label>
              <Select value={filterDepartment} onValueChange={(v) => { setFilterDepartment(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {uniqueDepartments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={(checked) => { setShowInactive(checked); setCurrentPage(1); }}
              />
              <Label htmlFor="show-inactive" className="text-sm text-slate-500">
                Show inactive users
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="backdrop-blur-xl bg-white/70 dark:bg-slate-900/70 border-white/20 shadow-sm">
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            A list of all registered users including their name, role, and department.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 dark:border-slate-800">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-slate-300"
                    />
                  </TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id} className={user.is_active === 0 ? "opacity-60" : ""}>
                      <TableCell>
                        {user.role !== 'superadmin' && (
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => handleSelectUser(user.id)}
                            className="rounded border-slate-300"
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar?.startsWith('http') ? user.avatar : undefined} />
                            <AvatarFallback>{user.avatar || user.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span>{user.name}</span>
                            <span className="text-xs text-slate-500">{user.email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getRoleBadgeClass(user.role)}>
                          {(user.role === 'admin' || user.role === 'superadmin') && <ShieldCheck className="w-3 h-3 mr-1 inline" />}
                          {user.role === 'superadmin' ? 'Super Admin' : user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>{user.department}</TableCell>
                      <TableCell>
                        <Badge variant={user.is_active !== 0 ? "default" : "secondary"} className={user.is_active !== 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                          {user.is_active !== 0 ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {user.joinedAt ? new Date(user.joinedAt).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <DotsThree className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => {
                              setProfileUserId(user.id);
                              setIsProfileOpen(true);
                            }}>
                              <IdentificationCard className="mr-2 h-4 w-4" />
                              View Profile
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEditRole(user)}>
                              <PencilSimple className="mr-2 h-4 w-4" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                              <Key className="mr-2 h-4 w-4" />
                              Reset Password
                            </DropdownMenuItem>
                            {onInitiateOffboarding && user.is_active !== 0 && (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setOffboardingUser(user);
                                  setIsOffboardingDialogOpen(true);
                                }}
                                className="text-orange-600 focus:text-orange-600"
                              >
                                <SignOut className="mr-2 h-4 w-4" />
                                Initiate Offboarding
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {user.is_active === 0 ? (
                              <DropdownMenuItem
                                className="text-green-600 focus:text-green-600"
                                onClick={() => handleActivateUser(user.id)}
                              >
                                <UserGear className="mr-2 h-4 w-4" />
                                Activate User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={user.role === 'superadmin'}
                              >
                                <Trash className="mr-2 h-4 w-4" />
                                Deactivate User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * usersPerPage) + 1} to {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-500">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update role and department for {editingUser?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                <strong>Super Admin:</strong> Full unrestricted access.<br />
                <strong>Admin:</strong> System management (cannot manage Super Admins).<br />
                <strong>HR:</strong> User management and HR operations.<br />
                <strong>Manager:</strong> Access to team view and reports.<br />
                <strong>Employee:</strong> Standard access to attendance and tasks.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={newDepartment} onValueChange={setNewDepartment}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveUserChanges}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetPasswordDialogOpen} onOpenChange={setIsResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {resetPasswordUser?.name}. They will need to use this password to log in.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500">Password must be at least 6 characters.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResetPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveNewPassword}>Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Operations Dialog */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Operations</DialogTitle>
            <DialogDescription>
              Apply changes to {selectedUsers.size} selected user(s)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={bulkAction} onValueChange={(v: any) => setBulkAction(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">Change Role</SelectItem>
                  <SelectItem value="department">Change Department</SelectItem>
                  <SelectItem value="activate">Activate Users</SelectItem>
                  <SelectItem value="deactivate">Deactivate Users</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bulkAction === 'role' && (
              <div className="space-y-2">
                <Label>New Role</Label>
                <Select value={bulkRole} onValueChange={setBulkRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="hr">HR</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {bulkAction === 'department' && (
              <div className="space-y-2">
                <Label>New Department</Label>
                <Select value={bulkDepartment} onValueChange={setBulkDepartment}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(bulkAction === 'activate' || bulkAction === 'deactivate') && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {bulkAction === 'deactivate'
                    ? "⚠️ Selected users will no longer be able to log in."
                    : "✓ Selected users will be reactivated and able to log in."}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkAction}>
              Apply to {selectedUsers.size} User(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offboarding Dialog */}
      <Dialog open={isOffboardingDialogOpen} onOpenChange={setIsOffboardingDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Initiate Employee Offboarding</DialogTitle>
            <DialogDescription>
              Start the offboarding process for {offboardingUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="last-working-day">Last Working Day *</Label>
              <Input
                id="last-working-day"
                type="date"
                value={offboardingForm.lastWorkingDay}
                onChange={(e) => setOffboardingForm({ ...offboardingForm, lastWorkingDay: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Select
                value={offboardingForm.reason}
                onValueChange={(value) => setOffboardingForm({ ...offboardingForm, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="resignation">Resignation</SelectItem>
                  <SelectItem value="retirement">Retirement</SelectItem>
                  <SelectItem value="termination">Termination</SelectItem>
                  <SelectItem value="end-of-contract">End of Contract</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason-details">Additional Details</Label>
              <textarea
                id="reason-details"
                className="w-full min-h-[100px] rounded-md border border-slate-300 p-3"
                placeholder="Enter any additional details or notes..."
                value={offboardingForm.reasonDetails}
                onChange={(e) => setOffboardingForm({ ...offboardingForm, reasonDetails: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsOffboardingDialogOpen(false);
                setOffboardingForm({ lastWorkingDay: '', reason: 'resignation', reasonDetails: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (offboardingUser && offboardingForm.lastWorkingDay && onInitiateOffboarding) {
                  onInitiateOffboarding(
                    offboardingUser,
                    offboardingForm.lastWorkingDay,
                    offboardingForm.reason,
                    offboardingForm.reasonDetails || undefined
                  );
                  setIsOffboardingDialogOpen(false);
                  setOffboardingForm({ lastWorkingDay: '', reason: 'resignation', reasonDetails: '' });
                  setOffboardingUser(null);
                } else {
                  toast.error('Please fill in all required fields');
                }
              }}
            >
              Initiate Offboarding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Profile Dialog */}
      <UserProfile
        userId={profileUserId || ''}
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />
    </div>
  );
}
